import React, { useState, useCallback, useMemo } from 'react'
import { ActivityIndicator, StyleSheet, TouchableOpacity, View, Alert } from 'react-native'
import { useTheme, useNavigation, useRoute, RouteProp } from '@react-navigation/native'
import { Button, Icon, Screen, Text } from '@components'
import { Theme } from '@theme'
import { useLanguage } from '@context'
import {
  useFtGetSocialImportStatus,
  useFtGetSocialImportResults,
  useFtValidateSocialImport,
} from '@hooks'
import { translateApiError } from '@utils'
import type { ScrapedProfile } from '@hooks'
import { ScrapedProfilesList } from './components/ScrapedProfilesList'
import type { ContactStackParamList } from '../../../navigation/navigator/ContactNavigator'

const networkLabels: Record<string, string> = {
  instagram: 'Instagram',
  facebook: 'Facebook',
  twitter: 'Twitter/X',
  linkedin: 'LinkedIn',
}

export const FtSocialImportResults = () => {
  const theme = useTheme()
  const { t } = useLanguage()
  const styles = getStyles(theme)
  const navigation = useNavigation()
  const route = useRoute<RouteProp<ContactStackParamList, 'FtSocialImportResults'>>()
  const { importId } = route.params

  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set())

  const { data: statusData } = useFtGetSocialImportStatus(importId)
  const { data: resultsData, isLoading: isLoadingResults } = useFtGetSocialImportResults(
    importId,
    0,
    100,
    statusData?.status === 'succeeded',
  )
  const validateImport = useFtValidateSocialImport()

  const network = statusData?.network ?? ''
  const networkLabel = networkLabels[network] ?? network
  const isRunning = statusData?.status === 'running' || statusData?.status === 'pending'
  const isSucceeded = statusData?.status === 'succeeded'
  const isFailed = statusData?.status === 'failed' || statusData?.status === 'timed_out'

  const profiles = resultsData?.items ?? []
  const alreadyImportedUsernames = useMemo(
    () => new Set(resultsData?.alreadyImportedUsernames ?? []),
    [resultsData?.alreadyImportedUsernames],
  )

  const selectableProfiles = useMemo(
    () => profiles.filter((p) => !(p.username && alreadyImportedUsernames.has(p.username))),
    [profiles, alreadyImportedUsernames],
  )

  const handleToggleProfile = useCallback(
    (index: number) => {
      const profile = profiles[index]
      if (profile.username && alreadyImportedUsernames.has(profile.username)) return

      setSelectedIds((prev) => {
        const next = new Set(prev)
        if (next.has(index)) {
          next.delete(index)
        } else {
          next.add(index)
        }
        return next
      })
    },
    [profiles, alreadyImportedUsernames],
  )

  const handleSelectAll = useCallback(() => {
    const selectableIndices = profiles
      .map((p, i) => ({ p, i }))
      .filter(({ p }) => !(p.username && alreadyImportedUsernames.has(p.username)))
      .map(({ i }) => i)

    if (selectedIds.size === selectableIndices.length) {
      setSelectedIds(new Set())
    } else {
      setSelectedIds(new Set(selectableIndices))
    }
  }, [selectedIds.size, profiles, alreadyImportedUsernames])

  const handleValidate = useCallback(async () => {
    if (selectedIds.size === 0) return

    const selectedProfiles = Array.from(selectedIds).map((index) => {
      const p = profiles[index]
      return {
        fullName:
          p.fullName ||
          [p.firstName, p.lastName].filter(Boolean).join(' ') ||
          p.username ||
          'Unknown',
        username: p.username ?? undefined,
        avatarUrl: p.avatarUrl ?? undefined,
        bio: p.bio ?? undefined,
        profileUrl: p.profileUrl ?? undefined,
      }
    })

    try {
      await validateImport.mutateAsync({ importId, selectedProfiles })
      Alert.alert(
        t('ftSocialImport.results.successTitle'),
        t('ftSocialImport.results.successMessage', { count: selectedProfiles.length }),
      )
      navigation.goBack()
    } catch (e: unknown) {
      Alert.alert(t('ftSocialImport.error.title'), translateApiError(t, e).message)
    }
  }, [importId, selectedIds, profiles, validateImport, navigation, t])

  return (
    <Screen>
      <View style={styles.header}>
        <Icon name="arrowBack" onPress={() => navigation.goBack()} />
        <Text type="titleBold" color={theme.colors.black}>
          {t('ftSocialImport.results.title', { network: networkLabel })}
        </Text>
        <View style={{ width: 24 }} />
      </View>

      {isRunning && (
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color={theme.colors.primary} />
          <Text type="body" color={theme.colors.secondary} style={styles.loadingText}>
            {t('ftSocialImport.inProgress')}
          </Text>
          <Text type="bodyExtraSmall" color={theme.colors.lightGrey}>
            {t('ftSocialImport.results.runningHint')}
          </Text>
        </View>
      )}

      {isFailed && (
        <View style={styles.loadingContainer}>
          <Text type="body" color={theme.colors.error}>
            {t('ftSocialImport.results.failedTitle')}
          </Text>
          <Text type="bodyExtraSmall" color={theme.colors.lightGrey}>
            {statusData?.errorMessage || t('ftSocialImport.error.genericError')}
          </Text>
        </View>
      )}

      {isSucceeded && (
        <View style={styles.resultsContainer}>
          <View style={styles.resultsHeader}>
            <Text type="body" color={theme.colors.secondary}>
              {t('ftSocialImport.profilesFound', { count: resultsData?.total ?? 0 })}
            </Text>
            <TouchableOpacity onPress={handleSelectAll}>
              <Text type="body" color={theme.colors.primary}>
                {selectedIds.size === selectableProfiles.length && selectableProfiles.length > 0
                  ? t('ftSocialImport.results.deselectAll')
                  : t('ftSocialImport.results.selectAll')}
              </Text>
            </TouchableOpacity>
          </View>

          {isLoadingResults ? (
            <View style={styles.loadingContainer}>
              <ActivityIndicator size="large" color={theme.colors.primary} />
            </View>
          ) : (
            <ScrapedProfilesList
              profiles={profiles}
              selectedIds={selectedIds}
              onToggle={handleToggleProfile}
              disabledUsernames={alreadyImportedUsernames}
            />
          )}

          <View style={styles.footer}>
            <Button
              title={t('ftSocialImport.results.validate', { count: selectedIds.size })}
              onPress={handleValidate}
              disabled={selectedIds.size === 0 || validateImport.isPending}
              containerStyle={styles.button}
            />
          </View>
        </View>
      )}
    </Screen>
  )
}

const getStyles = (theme: Theme) =>
  StyleSheet.create({
    header: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      paddingHorizontal: theme.spacing.md,
      paddingVertical: theme.spacing.md,
    },
    loadingContainer: {
      flex: 1,
      alignItems: 'center',
      justifyContent: 'center',
      gap: theme.spacing.md,
    },
    loadingText: {
      marginTop: theme.spacing.sm,
    },
    resultsContainer: {
      flex: 1,
    },
    resultsHeader: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      paddingHorizontal: theme.spacing.lg,
      paddingVertical: theme.spacing.sm,
    },
    footer: {
      paddingHorizontal: theme.spacing.lg,
      paddingVertical: theme.spacing.md,
      borderTopWidth: 1,
      borderTopColor: theme.colors.grey,
    },
    button: {
      marginTop: 0,
    },
  })
